import { useContext, useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { Context } from '@/context';
import { notifyError, notifySuccess, notifyWarning } from '@/notifications/notify';
import CustomDrawerSections from '@/DesignSystem/components/drawer/CustomDrawerSections';
import ComentarioForm from './ComentarioForm';
import ComentarioCard from './ComentarioCard';
import { getTimeAgo } from '@/helpers/datetime-helper';
import LoginCompacto from '@/components/shared/login/LoginCompacto';
import { LOTTIE_ICONS } from '@/DesignSystem/features/lottie/lottie-icons';
import LottieIconComponent from '@/DesignSystem/features/lottie/LottieIconComponent';

const STORAGE_KEY = 'comentarios_likes';
const MAX_COMMENT_LENGTH = 280;

const ComentariosDrawer = ({ open, onClose, slug, onCommentAdded, onCommentDeleted }) => {
    const { state } = useContext(Context);
    const { user } = state;
    const isLoggedIn = user?.logged && user?.token;

    const [comentarios, setComentarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [likedComments, setLikedComments] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [expandedComments, setExpandedComments] = useState([]);
    const [viewportMetrics, setViewportMetrics] = useState({
        height: '100dvh',
        offsetTop: 0,
    });

    useEffect(() => {
        if (open) {
            fetchComentarios();
            loadLikesFromStorage();
        }
    }, [open, slug]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const viewport = window.visualViewport;

        const updateViewportMetrics = () => {
            const height = viewport ? viewport.height : window.innerHeight;
            const offsetTop = viewport ? viewport.offsetTop : 0;

            setViewportMetrics({
                height: `${height}px`,
                offsetTop,
            });
        };

        updateViewportMetrics();

        if (viewport) {
            viewport.addEventListener('resize', updateViewportMetrics);
            viewport.addEventListener('scroll', updateViewportMetrics);
        } else {
            window.addEventListener('resize', updateViewportMetrics);
        }

        return () => {
            if (viewport) {
                viewport.removeEventListener('resize', updateViewportMetrics);
                viewport.removeEventListener('scroll', updateViewportMetrics);
            } else {
                window.removeEventListener('resize', updateViewportMetrics);
            }
        };
    }, [open]);

    const loadLikesFromStorage = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                setLikedComments(JSON.parse(stored));
            }
        } catch (error) {
            console.error('Error loading likes from storage:', error);
        }
    };

    const saveLikeToStorage = (comentarioId, type) => {
        try {
            const updated = { ...likedComments, [comentarioId]: type };
            setLikedComments(updated);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (error) {
            console.error('Error saving like to storage:', error);
        }
    };

    const fetchComentarios = async () => {
        setLoading(true);
        try {
            const response = await axiosApiInstance.get(URL_FACTORY.GetComentariosBySlug(slug));
            const fetchedComentarios = response.data.comentarios || [];
            const sortedComentarios = [...fetchedComentarios].sort(
                (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
            );
            setComentarios(sortedComentarios);
        } catch (error) {
            console.error('Error al cargar comentarios:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitComment = async () => {
        if (!newComment.trim()) {
            notifyError('El comentario no puede estar vacío');
            return;
        }

        if (!isLoggedIn) {
            notifyError('Debes iniciar sesión para comentar');
            return;
        }

        setSubmitting(true);
        try {
            await axiosApiInstance.post(URL_FACTORY.CreateComentario, {
                slug,
                content: newComment,
                replyToUserId: replyTo?.userId,
                replyToNickname: replyTo?.nickname,
            });
            setNewComment('');
            setReplyTo(null);
            notifySuccess('Comentario publicado exitosamente');
            await fetchComentarios();

            if (onCommentAdded) {
                onCommentAdded();
            }
        } catch (error) {
            console.error('Error al publicar comentario:', error);
            const errorMessage = error?.response?.data?.message || 'Error al publicar comentario';

            if (errorMessage.includes('no permitido') || errorMessage.includes('Solo se permiten')) {
                notifyWarning(errorMessage);
            } else {
                notifyError(errorMessage);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const toggleCommentExpansion = useCallback((comentarioId) => {
        setExpandedComments((prev) => {
            if (prev.includes(comentarioId)) {
                return prev.filter((id) => id !== comentarioId);
            }
            return [...prev, comentarioId];
        });
    }, []);

    const ensureCommentExpanded = useCallback((comentarioId) => {
        setExpandedComments((prev) => {
            if (prev.includes(comentarioId)) {
                return prev;
            }
            return [...prev, comentarioId];
        });
    }, []);

    const handleEditComment = async (id) => {
        if (!editContent.trim()) {
            notifyError('El comentario no puede estar vacío');
            return;
        }

        try {
            await axiosApiInstance.put(URL_FACTORY.UpdateComentario(id), {
                content: editContent,
            });
            setEditingId(null);
            setEditContent('');
            notifySuccess('Comentario actualizado');
            await fetchComentarios();
        } catch (error) {
            console.error('Error al actualizar comentario:', error);
            const errorMessage = error?.response?.data?.message || 'Error al actualizar comentario';

            if (errorMessage.includes('no permitido') || errorMessage.includes('Solo se permiten')) {
                notifyWarning(errorMessage);
            } else {
                notifyError(errorMessage);
            }
        }
    };

    const handleDeleteComment = async (id) => {
        try {
            await axiosApiInstance.delete(URL_FACTORY.DeleteComentario(id));
            notifySuccess('Comentario eliminado');
            await fetchComentarios();

            if (onCommentDeleted) {
                onCommentDeleted();
            }
        } catch (error) {
            console.error('Error al eliminar comentario:', error);
            notifyError(error?.response?.data?.message || 'Error al eliminar comentario');
        }
    };

    const handleLike = async (comentarioId, isLike) => {
        const currentState = likedComments[comentarioId];

        if (!isLoggedIn) {
            const newType =
                currentState === (isLike ? 'like' : 'dislike') ? null : isLike ? 'like' : 'dislike';
            saveLikeToStorage(comentarioId, newType);

            setComentarios((prev) =>
                prev.map((c) => {
                    if (c.id === comentarioId) {
                        const updated = { ...c };
                        if (currentState === 'like') {
                            updated.likesCount = Math.max(0, updated.likesCount - 1);
                        } else if (currentState === 'dislike') {
                            updated.dislikesCount = Math.max(0, updated.dislikesCount - 1);
                        }

                        if (newType === 'like') {
                            updated.likesCount++;
                        } else if (newType === 'dislike') {
                            updated.dislikesCount++;
                        }

                        return updated;
                    }
                    return c;
                })
            );
            return;
        }

        try {
            await axiosApiInstance.post(URL_FACTORY.ToggleLikeComentario(comentarioId, isLike));

            const newType =
                currentState === (isLike ? 'like' : 'dislike') ? null : isLike ? 'like' : 'dislike';
            saveLikeToStorage(comentarioId, newType);

            await fetchComentarios();
        } catch (error) {
            console.error('Error al dar like:', error);
            notifyError('Error al procesar la acción');
        }
    };

    const startReply = (comentario) => {
        setReplyTo({
            userId: comentario.userId,
            nickname: comentario.account?.nickname || 'Usuario',
        });
        setNewComment(`@${comentario.account?.nickname || 'Usuario'} `);
        ensureCommentExpanded(comentario.id);
    };

    const startEdit = (comentario) => {
        setEditingId(comentario.id);
        setEditContent(comentario.content);
        ensureCommentExpanded(comentario.id);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditContent('');
    };

    const cancelReply = () => {
        setReplyTo(null);
        setNewComment('');
    };

    const handleEditContentChange = (value) => {
        if (value.length <= MAX_COMMENT_LENGTH) {
            setEditContent(value);
        }
    };

    const handleNewCommentChange = (value) => {
        if (value.length <= MAX_COMMENT_LENGTH) {
            setNewComment(value);
        }
    };

    const footerContent = isLoggedIn ? (
        <ComentarioForm
            replyTo={replyTo}
            onCancelReply={cancelReply}
            newComment={newComment}
            onChangeNewComment={handleNewCommentChange}
            onSubmit={handleSubmitComment}
            submitting={submitting}
            maxCommentLength={MAX_COMMENT_LENGTH}
        />
    ) : (
        <Box>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                Debes iniciar sesión para participar
            </Typography>
            <LoginCompacto onSuccess={() => fetchComentarios()} compact />
        </Box>
    );

    return (
        <CustomDrawerSections
            open={open}
            onClose={onClose}
            title={`Comentarios: ${comentarios.length}`}
            anchor="right"
            paperSx={{
                height: viewportMetrics.height,
                maxHeight: viewportMetrics.height,
                top: viewportMetrics.offsetTop,
                overflow: 'hidden',
            }}
            contentProps={{ sx: { py: 2, px: 2 } }}
            footer={footerContent}
            footerProps={{ sx: { py: 2, px: 2 } }}
        >
            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 3,
                        height: '100%',
                    }}
                >
                    <LottieIconComponent icon={LOTTIE_ICONS.LOADING} width={100} height={100} />
                </Box>
            ) : comentarios.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No hay comentarios aún. ¡Sé el primero en comentar!
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ overflowX: 'hidden' }}>
                    {comentarios.map((comentario) => {
                        const userLikeState = likedComments[comentario.id];
                        const hasLiked = userLikeState === 'like';
                        const hasDisliked = userLikeState === 'dislike';
                        const isExpanded =
                            expandedComments.includes(comentario.id) || editingId === comentario.id;

                        return (
                            <Box key={comentario.id} sx={{ mb: 0.25 }}>
                                <ComentarioCard
                                    comentario={comentario}
                                    isExpanded={isExpanded}
                                    onToggle={() => toggleCommentExpansion(comentario.id)}
                                    isOwner={isLoggedIn && user?.nickname === comentario.account?.nickname}
                                    isLoggedIn={isLoggedIn}
                                    hasLiked={hasLiked}
                                    hasDisliked={hasDisliked}
                                    onLike={() => handleLike(comentario.id, true)}
                                    onDislike={() => handleLike(comentario.id, false)}
                                    onReply={() => startReply(comentario)}
                                    onEditClick={() => startEdit(comentario)}
                                    onDelete={() => handleDeleteComment(comentario.id)}
                                    isEditing={editingId === comentario.id}
                                    editContent={editContent}
                                    onChangeEditContent={handleEditContentChange}
                                    onSaveEdit={() => handleEditComment(comentario.id)}
                                    onCancelEdit={cancelEdit}
                                    maxCommentLength={MAX_COMMENT_LENGTH}
                                    formattedDate={getTimeAgo(comentario.created)}
                                    replyingTo={comentario.replyToNickname}
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </CustomDrawerSections>
    );
};

export default ComentariosDrawer;
